import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { RedactionAnnotationEdit } from "../../../../review/redaction-annotation/redaction-annotation-edit.model";
import { RedactionAnnotation } from "../../../../review/redaction-annotation/redaction-annotation.model";
import { LifeCycleEdit } from "../../../../review/redaction-annotation/life-cycle-edit.enum";
import { Store } from '@ngrx/store';
import { BaseComponent } from 'src/app/components/base.component';
import { Observable, map, takeUntil } from 'rxjs';
import { currentRedactionId, maxRedactionId } from '@store/doc-viewer/redaction-editor/redaction-editor.selectors';
import { currentRedactionActions } from '@store/doc-viewer/redaction-editor/redaction-editor.actions';
import { currentAnnotationId, maxAnnotationId } from '@store/doc-viewer/annotation-editor/annotation-editor.selectors';
import { currentAnnotationActions } from '@store/doc-viewer/annotation-editor/annotation-editor.actions';
import { Canvas, Group, GroupProps, Pattern, Rect, Textbox } from "fabric";

@Component({
    selector: 'lex-draw-box',
    templateUrl: './draw-box.component.html',
    styleUrls: ['./draw-box.component.scss'],
    standalone: false
})
export class DrawBoxComponent extends BaseComponent implements AfterViewInit, OnChanges, OnDestroy {
	private currentSelectAnnotatedId$: Observable<number> = this.store.select(currentAnnotationId);
	private currentSelectedRedactedId$: Observable<number> = this.store.select(currentRedactionId());
	private maxAnnotationId$: Observable<number> = this.store.select(maxAnnotationId);
	private maxRedactionId$: Observable<number> = this.store.select(maxRedactionId);
	private maxRedactionId: number;
	private maxAnnotationId: number;
	private canvasInstance: Canvas;
	private currentRedactionId: number;
	private currentAnnotationId: number;
	private fabricPattern: Pattern;
	private minWidth = 5;
	private minHeight = 5;
	private mouseDownDisposer: VoidFunction;
	private mouseUpDisposer: VoidFunction;

	@Input() canvasWidth: number;
	@Input() canvasHeight: number;
	@Input() edits: RedactionAnnotationEdit[];
	@Input() mode: 'annotation' | 'redaction' = 'redaction';
	@Output() editsChanged: EventEmitter<RedactionAnnotationEdit[]> = new EventEmitter<RedactionAnnotationEdit[]>();

	constructor(private store: Store) {
		super();

		this.currentSelectedRedactedId$.pipe(
			map((id) => this.currentRedactionId = id),
			takeUntil(this.destroy$)
		).subscribe();

		this.maxRedactionId$.pipe(
			map(max => this.maxRedactionId = max),
			takeUntil(this.destroy$)
		).subscribe();

		this.currentSelectAnnotatedId$.pipe(
			map((id) => this.currentAnnotationId = id),
			takeUntil(this.destroy$)
		).subscribe();

		this.maxAnnotationId$.pipe(
			map(max => this.maxAnnotationId = max),
			takeUntil(this.destroy$)
		).subscribe();

		this.initializePattern();
	}

	public ngOnDestroy(): void {
		this.mouseDownDisposer?.();
		this.mouseUpDisposer?.();
		this.canvasInstance.dispose();
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.edits) {
			setTimeout(() => this.redrawCanvas());
		}

		if (changes.canvasWidth || changes.canvasHeight) {
			this.canvasInstance?.setDimensions({
				width: this.canvasWidth,
				height: this.canvasHeight
			});
		}
	}

	public ngAfterViewInit() {
		let isCreatingBox = false;
		let moveDisposer: VoidFunction;
		this.canvasInstance = new Canvas('myCanvas', {
			width: this.canvasWidth,
			height: this.canvasHeight
		});
		this.canvasInstance.perPixelTargetFind = true;   // only fire when pointer is over painted pixels
		this.canvasInstance.targetFindTolerance = 0;     // no “halo” around shapes
		this.canvasInstance.uniformScaling = false;

		this.mouseDownDisposer = this.canvasInstance.on('mouse:down', (option) => {
			if (option.target) {
				return;
			} else {
				const startY = option.viewportPoint.y,
					startX = option.viewportPoint.x;

				const newRectangle = this.createNewRectangle(startY, startX);
				const newRedactionAnnotationEdit = this.createNewRedactionAnnotationEdit(newRectangle);
				this.edits.push(newRedactionAnnotationEdit);
				this.canvasInstance.add(newRectangle);
				isCreatingBox = true;
				this.canvasInstance.skipTargetFind = true;
				moveDisposer = this.canvasInstance.on('mouse:move', (option) => {
					// pointer is more 'accurate' with x,y coords than option.e, when dragging outside the canvas option.e's values jump around and are not accurate
					const pointer = this.canvasInstance.getViewportPoint(option.e);
					// Clamp offset values to the canvas boundaries
					const clampedX = Math.max(0, Math.min(this.canvasInstance.width, pointer.x));
					const clampedY = Math.max(0, Math.min(this.canvasInstance.height, pointer.y));
					let width = Math.round(clampedX - startX);
					let height = Math.round(clampedY - startY);
					// If width is negative, move 'left' to be on the left (instead of the right) and make width positive
					if (width < 0) {
						newRectangle.set('left', Math.round(startX + width));
						width = Math.abs(width);
					}
					// If height is negative, move 'top' to be on the top (instead of the bottom) and make height positive
					if (height < 0) {
						newRectangle.set('top', Math.round(startY + height));
						height = Math.abs(height);
					}
					newRectangle.set('width', width);
					newRectangle.set('height', height);
					newRectangle.set('strokeWidth', 2);
					newRectangle.setCoords();

					newRedactionAnnotationEdit.x = Math.floor(newRectangle.left);
					newRedactionAnnotationEdit.y = Math.floor(newRectangle.top);
					newRedactionAnnotationEdit.width = newRectangle.width;
					newRedactionAnnotationEdit.height = newRectangle.height;
				});
			}
		});

		this.mouseUpDisposer = this.canvasInstance.on('mouse:up', () => {
			moveDisposer?.();
			const newBox = this.edits.find(edit => edit.status === LifeCycleEdit.Initialized);
			const shouldDrawBox = (newBox?.width <= this.minWidth) && (newBox?.height <= this.minHeight);
			if (shouldDrawBox) {
				this.edits = this.edits.filter(edit => edit.clientId !== newBox.clientId);
				isCreatingBox = false;
				this.canvasInstance.skipTargetFind = false;
			}
			this.canvasInstance.renderAll();
			if (isCreatingBox) {
				this.editsChanged.emit(this.edits);
				isCreatingBox = false;
				this.canvasInstance.skipTargetFind = false;
			}
		});

	}

	private createNewRectangle(startY: number, startX: number) {
		const stroke = this.mode === 'redaction' ? '#ffc107' : '#def440';
		const newRectangle = new Rect({
			top: startY,
			left: startX,
			width: 0,
			height: 0,
			stroke: stroke,
			strokeWidth: 2,
			fill: this.fabricPattern
		});

		newRectangle.setControlsVisibility({mtr: false})
		return newRectangle;
	}

	private createNewRedactionAnnotationEdit(newRectangle: Rect) {
		const newRedactionAnnotation = new RedactionAnnotation();
		newRedactionAnnotation.text = "";
		newRedactionAnnotation.x = newRectangle.left;
		newRedactionAnnotation.y = newRectangle.top;
		newRedactionAnnotation.height = newRectangle.height;
		newRedactionAnnotation.width = newRectangle.width;

		const nextId = (this.mode === 'redaction' ? this.maxRedactionId : this.maxAnnotationId) + 1;
		const newRedactionAnnotationEdit = new RedactionAnnotationEdit(newRedactionAnnotation, nextId);
		newRedactionAnnotationEdit.status = LifeCycleEdit.Initialized;
		return newRedactionAnnotationEdit;
	}

	private redrawCanvas(): void {
		if (!this.canvasInstance) return;
		this.canvasInstance.clear();

		this.edits.forEach((edit) => {
			if (this.mode === 'redaction')
				this.createRedactionObject(edit);
			else if (this.mode === 'annotation')
				this.createAnnotationObject(edit);
		});
	}

	private createRedactionObject(edit: RedactionAnnotationEdit) {
		const rectangle = new Rect({
			left: edit.x,
			top: edit.y,
			width: edit.width,
			height: edit.height,
			stroke: '#ffc107',
			strokeWidth: 2,
			strokeUniform: true,
			noScaleCache: false,
			fill: this.getBackgroundColor(edit.highlightColor),
			id: edit.clientId,
		} as CustomIRectOptions);

		const textColor = edit.highlightColor === '0c0d01' ? 'white' : 'black';
		const text = new Textbox(edit.text || "", {
			fontSize: 14,
			fill: textColor,
			fontFamily: 'Roboto, sans-serif',
			selectable: false,
			lockUniScaling: false,
			noScaleCache: false,
			hasControls: false,
			hasBorders: false,
			textAlign: 'center',
		});

		const group = new Group([rectangle, text], {
			left: edit.x,
			top: edit.y,
			strokeWidth: 0,
			width: edit.width,
			height: edit.height,
			strokeUniform: true,
			noScaleCache: false,
			cornerColor: `#FFFFFF`,
			cornerStrokeColor: 'black',
			cornerSize: 12,
			cornerStyle: 'circle',
			transparentCorners: false,
			id: edit.clientId,
		} as CustomIRectOptions);


		// Hide text when scaling
		group.on('scaling', () => {
			text.set('opacity', 0);
			this.canvasInstance.renderAll();
		});

		group.setControlsVisibility({mtr: false})
		group.on("mouseup", (e) => {
			this.currentRedactionId = edit.clientId;
			if (!e.target) return;
			const target = e.target;

			// Always update the position from the target
			const newX = Math.floor(target.left);
			const newY = Math.floor(target.top);

			// If the scale factors differ significantly from 1, update dimensions.
			if (Math.abs(target.scaleX - 1) >= 0.01 || Math.abs(target.scaleY - 1) >= 0.01) {
				const newWidth = target.getScaledWidth();
				const newHeight = target.getScaledHeight();
				edit.width = Math.ceil(newWidth);
				edit.height = Math.ceil(newHeight);
			}

			// Reset scale to prevent compounding
			target.scaleX = 1;
			target.scaleY = 1;

			// Always update the edit's position, even if no scaling occurred.
			edit.x = newX;
			edit.y = newY;
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			// Emit the update so the new box (or its new position) persists.
			this.editsChanged.emit(this.edits);
		});

		group.on("mouseover", (e) => {
			if (this.currentRedactionId === edit.clientId) return;
			this.currentRedactionId = edit.clientId;
			if (!e.target) return;
			this.store.dispatch(currentRedactionActions.setCurrentRedaction({redactionId: this.currentRedactionId}));
		});

		this.canvasInstance.add(group);

		text.set({
			originX: 'center',
			originY: 'center',
			left: 0,
			top: 0,
			width: edit.width - 20,
			height: edit.height - 20,
			textAlign: 'center'
		});

		rectangle.set({
			top: 0,
			left: 0,
			originX: 'center',
			originY: 'center'
		});

		group.set({
			width: edit.width,
			height: edit.height
		});

		let changed = false;

		if (edit.height < text.height) {
			edit.height = Math.ceil(text.height + 5);
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			changed = true;
		}

		if (edit.width < text.width) {
			edit.width = Math.ceil(text.width + 15);
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			changed = true;
		}
		if (changed) {
			this.editsChanged.emit(this.edits);
		}

		if (this.currentRedactionId === edit.clientId) {
			this.canvasInstance.setActiveObject(group);
		}

		setTimeout(() => {
			this.canvasInstance.renderAll();
		})
	}

	private createAnnotationObject(edit: RedactionAnnotationEdit): void {
		const opacity = !edit.text ? .5 : 1;
		const rectangle = new Rect({
			width: edit.width,
			height: edit.height,
			stroke: this.getBackgroundColor(edit.highlightColor, opacity),
			strokeWidth: 2,
			strokeUniform: true,
			noScaleCache: false,
			fill: this.getBackgroundColor(edit.highlightColor, opacity),
			id: edit.clientId,
		} as CustomIRectOptions);

		const text = new Textbox(edit.text || "", {
			fontSize: 14,
			fill: 'black',
			fontFamily: 'Roboto, sans-serif',
			selectable: false,
			lockUniScaling: false,
			noScaleCache: false,
			hasControls: false,
			hasBorders: false,
			width: edit.width - 5,
			height: edit.height - 5,
			textAlign: 'left',
		});

		const group = new Group([rectangle, text], {
			left: edit.x,
			top: edit.y,
			strokeWidth: 0,
			width: edit.width,
			height: edit.height,
			strokeUniform: true,
			noScaleCache: false,
			cornerColor: `#FFFFFF`,
			cornerStrokeColor: 'black',
			cornerSize: 12,
			cornerStyle: 'circle',
			transparentCorners: false,
			id: edit.clientId,
		} as CustomIRectOptions);

		// Hide text when scaling
		group.on('scaling', () => {
			text.set('opacity', 0);
			this.canvasInstance.renderAll();
		});

		group.setControlsVisibility({mtr: false})
		group.on("mouseup", (e) => {
			this.currentAnnotationId = edit.clientId;
			if (!e.target) return;
			const target = e.target;

			// Always update the position from the target
			const newX = Math.floor(target.left);
			const newY = Math.floor(target.top);

			// If the scale factors differ significantly from 1, update dimensions.
			if (Math.abs(target.scaleX - 1) >= 0.01 || Math.abs(target.scaleY - 1) >= 0.01) {
				const newWidth = target.getScaledWidth();
				const newHeight = target.getScaledHeight();
				edit.width = Math.ceil(newWidth);
				edit.height = Math.ceil(newHeight);
			}

			// Reset scale to prevent compounding
			target.scaleX = 1;
			target.scaleY = 1;

			// Always update the edit's position, even if no scaling occurred.
			edit.x = newX;
			edit.y = newY;
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			// Emit the update so the new box (or its new position) persists.
			this.editsChanged.emit(this.edits);
		});
		group.on("mouseover", (e) => {
			if (this.currentAnnotationId === edit.clientId) return;
			this.currentAnnotationId = edit.clientId;
			if (!e.target) return;
			this.store.dispatch(currentAnnotationActions.setCurrentAnnotation({annotationId: this.currentAnnotationId}));
		});
		this.canvasInstance.add(group);

		text.set({
			originX: 'center',
			originY: 'center',
			left: 0,
			top: 0,
			width: edit.width - 20,
			height: edit.height - 20,
			textAlign: 'center'
		});

		rectangle.set({
			top: 0,
			left: 0,
			originX: 'center',
			originY: 'center'
		});

		group.set({
			width: edit.width,
			height: edit.height
		});

		let changed = false;

		if (edit.height < text.height) {
			edit.height = Math.ceil(text.height + 5);
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			changed = true;
		}

		if (edit.width < text.width) {
			edit.width = Math.ceil(text.width + 15);
			edit.status = edit.status === LifeCycleEdit.Created ? LifeCycleEdit.Created : LifeCycleEdit.Modified;
			changed = true;
		}
		if (changed) {
			this.editsChanged.emit(this.edits);
		}

		if (this.currentAnnotationId === edit.clientId) {
			this.canvasInstance.setActiveObject(group);
		}

		setTimeout(() => {
			this.canvasInstance.renderAll();
		})
	}

	private getBackgroundColor(colorValue: string, opacity: number = 0.5): string {
		if (colorValue === 'ffffff')
			return `rgba(255, 255, 255, ${opacity})`;
		if (colorValue === '0c0d01')
			return `rgba(0, 0, 0, ${opacity})`;
		if (colorValue === 'def440')
			return `rgba(222, 244, 64, ${opacity})`;
		if (colorValue === 'fadc64')
			return `rgba(250, 220, 100, ${opacity})`;
		if (colorValue === '99fb6b')
			return `rgba(153, 251, 107, ${opacity})`;
		if (colorValue === '6be1fb')
			return `rgba(107, 225, 251, ${opacity})`;
		if (colorValue === 'ee7eeb')
			return `rgba(238, 126, 235, ${opacity})`;
	}

	private initializePattern(): void {
		const patternCanvas = document.createElement('canvas');
		const patternContext = patternCanvas.getContext('2d');

		patternCanvas.width = 100;
		patternCanvas.height = 100;
		patternContext.fillStyle = this.mode === 'redaction' ? 'rgba(0, 0, 0, 0.5)' : 'rgb(222, 244, 64)';
		patternContext.fillRect(0, 0, patternCanvas.width, patternCanvas.height);
		this.fabricPattern = new Pattern({
			source: patternCanvas as any,
			repeat: 'repeat'
		});
	}
}

interface CustomIRectOptions extends Partial<GroupProps> {
	id: number | string;
}
